/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  RotatedObservabilityKey,
  RotatedObservabilityKey$inboundSchema,
} from "./rotatedobservabilitykey.js";

/**
 * What happened to previous observability plugin hooks keys.
 */
export const RotateObservabilityCredentialResultPreviousKeyFate = {
  RevokeImmediately: "revoke_immediately",
  Grace: "grace",
} as const;
/**
 * What happened to previous observability plugin hooks keys.
 */
export type RotateObservabilityCredentialResultPreviousKeyFate = ClosedEnum<
  typeof RotateObservabilityCredentialResultPreviousKeyFate
>;

export type RotateObservabilityCredentialResult = {
  /**
   * The newly minted hooks-scoped API key. Returned only on this response.
   */
  key: string;
  /**
   * The recognizable prefix of the new key.
   */
  keyPrefix: string;
  /**
   * Whether the published marketplace was updated with the new credential.
   */
  marketplaceRepublished: boolean;
  /**
   * True when a marketplace exists but could not be updated yet (for example the organization is not approved for the latest hooks version, or GitHub publishing is unavailable). Existing marketplace installs keep the previous credential until the marketplace is republished.
   */
  marketplaceUpdateDeferred?: boolean | undefined;
  /**
   * What happened to previous observability plugin hooks keys.
   */
  previousKeyFate: RotateObservabilityCredentialResultPreviousKeyFate;
  /**
   * Previous observability plugin hooks keys that were revoked or scheduled to expire.
   */
  previousKeys: Array<RotatedObservabilityKey>;
  /**
   * The latest deadline among previous keys when previous_key_fate is grace. Individual keys can expire earlier, so this is an upper bound rather than a shared deadline; per-key deadlines are on previous_keys.
   */
  previousKeysExpireAt?: Date | undefined;
  /**
   * Whether the chosen fate was applied to the previous keys. False means the replacement was created and published but retiring the previous keys failed, so they are still valid and the rotation should be retried.
   */
  previousKeysRetired: boolean;
};

/** @internal */
export const RotateObservabilityCredentialResultPreviousKeyFate$inboundSchema:
  z.ZodMiniEnum<typeof RotateObservabilityCredentialResultPreviousKeyFate> = z
    .enum(RotateObservabilityCredentialResultPreviousKeyFate);

/** @internal */
export const RotateObservabilityCredentialResult$inboundSchema: z.ZodMiniType<
  RotateObservabilityCredentialResult,
  unknown
> = z.pipe(
  z.object({
    key: z.string(),
    key_prefix: z.string(),
    marketplace_republished: z.boolean(),
    marketplace_update_deferred: z.optional(z.boolean()),
    previous_key_fate:
      RotateObservabilityCredentialResultPreviousKeyFate$inboundSchema,
    previous_keys: z.array(RotatedObservabilityKey$inboundSchema),
    previous_keys_expire_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    previous_keys_retired: z.boolean(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "key_prefix": "keyPrefix",
      "marketplace_republished": "marketplaceRepublished",
      "marketplace_update_deferred": "marketplaceUpdateDeferred",
      "previous_key_fate": "previousKeyFate",
      "previous_keys": "previousKeys",
      "previous_keys_expire_at": "previousKeysExpireAt",
      "previous_keys_retired": "previousKeysRetired",
    });
  }),
);

export function rotateObservabilityCredentialResultFromJSON(
  jsonString: string,
): SafeParseResult<RotateObservabilityCredentialResult, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) =>
      RotateObservabilityCredentialResult$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RotateObservabilityCredentialResult' from JSON`,
  );
}
